"use client";

import BulkImportArticle from "../../../components/BulkImportArticle";

const data = {
  title: "Bulk Import — Items",
  audience: "For: Anyone setting up the product/material/service catalogue in bulk",
  intro: [
    "Create many items at once from an Excel/CSV-style sheet. This flow creates the item records themselves — it does not set opening stock. Setting starting quantities is a separate step (Bulk Import — Opening Stock).",
  ],
  createsOnlyNote:
    "This flow creates the item only, with every warehouse at zero stock. It never sets opening quantities or posts an opening-stock journal — use Bulk Import — Opening Stock for that, after the items exist.",
  steps: {
    commitVerb: "Create the items",
    commitLabel: "Create",
    allOrNothing:
      "Creating is all-or-nothing: if even one row has a problem, no items are created — the whole batch is rolled back. Check first.",
  },
  requiredFields: [
    { field: "Item Name", needs: "Required; unique within the sheet and not already used in your company." },
    { field: "Item Type", needs: "One of: RM, FG, SEMI_RM, CONSUMABLE, TRADE, SCRAP." },
    { field: "Item Category", needs: "Goods or Service. (A Service item must have Track Inventory turned off.)" },
    { field: "Unit (UOM)", needs: "A valid unit code (e.g. NOS, PCS, KGS, LTR, BOX, MTR …)." },
    { field: "Item Group", needs: "Must match an existing Item Group name for your company." },
    { field: "GST", needs: "Required (free text). Note: it is stored but not linked to a tax master through this flow." },
    { field: "HSN/SAC Code", needs: "Required (free text)." },
    { field: "At least one role", needs: "Each item must be Sellable, Purchasable, or Track-Inventory — at least one." },
  ],
  conditionalFields: [
    { field: "If Sellable", rule: "Selling Price must be greater than 0, and a Sales Account name is required." },
    { field: "If Purchasable", rule: "Cost Price must be greater than 0, and a Purchase Account name is required." },
    { field: "If Track Inventory", rule: "An Inventory Account name is required." },
    { field: "Track Inventory (default ON)", rule: "If you don't set it, it defaults to ON — which then requires an Inventory Account. A Service item must set it OFF." },
    { field: "Fields for an OFF role", rule: "Leave price/account fields blank for any role that's off, or the row is rejected." },
    { field: "Description / Model No / Sub Item Group / Status", rule: "Optional." },
  ],
  matchFields: [
    { field: "Item Group", rule: "Must exactly match an existing Item Group name." },
    { field: "Sub Item Group", rule: "If provided, must exist. (It is not checked to belong to the chosen Item Group.)" },
    { field: "Sales Account", rule: "Must match an existing Income account." },
    { field: "Purchase Account", rule: "Must match an existing Expense / COGS / Other-Expense account." },
    { field: "Inventory Account", rule: "Must match an existing Stock account." },
  ],
  matchNote:
    "These must exactly match existing master records, case-sensitively (leading/trailing spaces are trimmed, but capitalization must match). Note that GST and HSN are accepted as free text and are not matched to any tax/HSN master.",
  duplicateRules: [
    { condition: "The same Item Name appears twice in your sheet", result: "Rejected — item names must be unique within the upload." },
    { condition: "The Item Name already exists in your company", result: "Rejected — it's already on record." },
    { condition: "Item number", result: "Not taken from the sheet — it is always auto-generated on create." },
  ],
  mistakes: [
    { problem: "Item Name missing or duplicated", meaning: "Every row needs a unique name." },
    { problem: "Item Type not one of the allowed values", meaning: "Use RM, FG, SEMI_RM, CONSUMABLE, TRADE, or SCRAP." },
    { problem: "Service item with Track Inventory on", meaning: "A service item cannot track inventory — set Track Inventory off (it defaults to on)." },
    { problem: "Sellable but no Selling Price / Sales Account", meaning: "A sellable item needs a price > 0 and a matching Income account." },
    { problem: "Account / Item Group / Sub Item Group name doesn't match", meaning: "Must match an existing record exactly (case-sensitive)." },
    { problem: "Unit or GST or Category missing", meaning: "These are required — a missing one rejects the whole sheet up front." },
  ],
  onApply: [
    "Each item gets a sequential item number using your company's Item numbering (default prefix I-).",
    "For every non-service item, an inventory record is created with all active warehouses seeded at zero stock and zero rate.",
    "Items are created in the same order as your sheet.",
    "GST is stored as text and is not linked to a tax rate through this flow; opening stock is not set here.",
  ],
  workflow: [
    "Build your sheet with the items you want to add.",
    "Make sure Item Groups and the Sales/Purchase/Inventory accounts you reference already exist, spelled exactly.",
    "Run the Check step first and review the problem rows.",
    "Fix each issue and re-check until every row passes.",
    "Run Create to add the items. Then use Bulk Import — Opening Stock if you need starting quantities.",
  ],
};

export default function BulkItemImportPage() {
  return <BulkImportArticle data={data} />;
}
